/**
 * LDV ghg_per_mile (metric tons CO2e per vehicle-mile) for GHG inventory.
 * Prefer EMFAC2025 county filtered (matches Policy Tool / current fleet); fill gaps from EMFAC2021.
 */
import type { SupabaseClient } from '@supabase/supabase-js';

export type LdvGhgPerMileYearRow = { year: number; ghg_per_mile: number };

function mergeLdvYearSeries(
  preferred: LdvGhgPerMileYearRow[],
  fallback: LdvGhgPerMileYearRow[]
): LdvGhgPerMileYearRow[] {
  const map = new Map<number, number>();
  for (const d of fallback) {
    const v = Number(d.ghg_per_mile);
    if (Number.isFinite(v) && v > 0) map.set(d.year, v);
  }
  for (const d of preferred) {
    const v = Number(d.ghg_per_mile);
    if (Number.isFinite(v) && v > 0) map.set(d.year, v);
  }
  return Array.from(map.entries())
    .map(([year, ghg_per_mile]) => ({ year, ghg_per_mile }))
    .sort((a, b) => a.year - b.year);
}

async function fetchLdvSeriesByGeofips(
  client: SupabaseClient,
  table: string,
  geofips: number
): Promise<LdvGhgPerMileYearRow[]> {
  const { data, error } = await client
    .from(table)
    .select('year, ghg_per_mile')
    .eq('geofips', geofips)
    .eq('class', 'LDV')
    .order('year', { ascending: true });
  if (error || !data?.length) return [];
  return data.map((item: { year: number; ghg_per_mile?: number | null }) => ({
    year: item.year,
    ghg_per_mile: Number(item.ghg_per_mile) || 0,
  }));
}

async function fetchLdvSeriesByLocationNames(
  client: SupabaseClient,
  table: string,
  locationNames: string[]
): Promise<LdvGhgPerMileYearRow[]> {
  for (const loc of locationNames) {
    const { data, error } = await client
      .from(table)
      .select('year, ghg_per_mile')
      .eq('location_name', loc)
      .eq('class', 'LDV')
      .order('year', { ascending: true });
    if (error || !data?.length) continue;
    return data.map((item: { year: number; ghg_per_mile?: number | null }) => ({
      year: item.year,
      ghg_per_mile: Number(item.ghg_per_mile) || 0,
    }));
  }
  return [];
}

/**
 * Fallback California series built from county rows when a dedicated CA row
 * (geofips=6 or location_name variants) is unavailable in EMFAC views.
 */
async function fetchCaliforniaAggregateFromCounties(
  client: SupabaseClient,
  table: string
): Promise<LdvGhgPerMileYearRow[]> {
  const { data, error } = await client
    .from(table)
    .select('year, total_ghg, total_vmt')
    .eq('class', 'LDV')
    .gte('geofips', 6000)
    .lte('geofips', 6999)
    .order('year', { ascending: true });

  if (error || !data?.length) return [];

  const byYear = new Map<number, { ghg: number; vmt: number }>();
  for (const row of data as Array<{ year: number; total_ghg?: number | null; total_vmt?: number | null }>) {
    const year = Number(row.year);
    const ghg = Number(row.total_ghg) || 0;
    const vmt = Number(row.total_vmt) || 0;
    if (!Number.isFinite(year) || year <= 0 || vmt <= 0) continue;
    const cur = byYear.get(year) || { ghg: 0, vmt: 0 };
    cur.ghg += ghg;
    cur.vmt += vmt;
    byYear.set(year, cur);
  }

  return Array.from(byYear.entries())
    .map(([year, sums]) => ({
      year,
      ghg_per_mile: sums.vmt > 0 ? sums.ghg / sums.vmt : 0,
    }))
    .filter((d) => Number.isFinite(d.ghg_per_mile) && d.ghg_per_mile > 0)
    .sort((a, b) => a.year - b.year);
}

/** California statewide rows in emfac*_county_filtered use geofips 6 in our pipeline. */
const CALIFORNIA_STATE_GEOFIPS = 6;

const mergedSeriesCache = new Map<
  string,
  Promise<{ countyData: LdvGhgPerMileYearRow[]; californiaData: LdvGhgPerMileYearRow[] }>
>();

/**
 * County + California average LDV tons/mile by year, merged 2025-over-2021.
 * Use `countyGeofips` when known (city → parent county, or county place); else resolve from `location`.
 *
 * @param client — Required. Use `supabaseClient` from `./supabase` in the browser. Node scripts must call
 *   after loading `.env` so `supabaseLocationService` (dynamic import) sees the same project.
 */
export async function fetchMergedEmfacLdvFactorsForInventory(
  location: string,
  client: SupabaseClient,
  countyGeofipsOverride?: number | null
): Promise<{ countyData: LdvGhgPerMileYearRow[]; californiaData: LdvGhgPerMileYearRow[] }> {
  const cacheKey = `${location}|${countyGeofipsOverride ?? ''}`;
  const cached = mergedSeriesCache.get(cacheKey);
  if (cached) return cached;

  const promise = fetchMergedEmfacLdvFactorsForInventoryUncached(location, client, countyGeofipsOverride);
  mergedSeriesCache.set(cacheKey, promise);
  return promise;
}

async function fetchMergedEmfacLdvFactorsForInventoryUncached(
  location: string,
  client: SupabaseClient,
  countyGeofipsOverride?: number | null
): Promise<{ countyData: LdvGhgPerMileYearRow[]; californiaData: LdvGhgPerMileYearRow[] }> {
  const { supabaseLocationService } = await import('./supabaseLocationService');
  await supabaseLocationService.initialize().catch(() => {});
  const resolvedCounty = (supabaseLocationService.getCountyForCity(location) || location).trim();
  const baseCountyName = resolvedCounty.replace(/\s+County$/i, '');
  const locationVariations = Array.from(
    new Set([`${baseCountyName} County`, baseCountyName, resolvedCounty])
  );

  let countyGeofips = countyGeofipsOverride ?? null;
  if (countyGeofips == null && location.trim() === 'California') {
    countyGeofips = CALIFORNIA_STATE_GEOFIPS;
  }

  let county21: LdvGhgPerMileYearRow[] = [];
  let county25: LdvGhgPerMileYearRow[] = [];
  if (countyGeofips != null) {
    [county21, county25] = await Promise.all([
      fetchLdvSeriesByGeofips(client, 'emfac2021_county_filtered', countyGeofips),
      fetchLdvSeriesByGeofips(client, 'emfac2025_county_filtered', countyGeofips),
    ]);
  }
  if (county21.length === 0 && county25.length === 0) {
    [county21, county25] = await Promise.all([
      fetchLdvSeriesByLocationNames(client, 'emfac2021_county_filtered', locationVariations),
      fetchLdvSeriesByLocationNames(client, 'emfac2025_county_filtered', locationVariations),
    ]);
  }

  const californiaVariations = ['California', 'CA', 'California State', 'State of California'];

  let ca21 = await fetchLdvSeriesByGeofips(client, 'emfac2021_county_filtered', CALIFORNIA_STATE_GEOFIPS);
  if (ca21.length === 0) {
    ca21 = await fetchLdvSeriesByLocationNames(client, 'emfac2021_county_filtered', californiaVariations);
  }
  if (ca21.length === 0) {
    ca21 = await fetchCaliforniaAggregateFromCounties(client, 'emfac2021_county_filtered');
  }
  let ca25 = await fetchLdvSeriesByGeofips(client, 'emfac2025_county_filtered', CALIFORNIA_STATE_GEOFIPS);
  if (ca25.length === 0) {
    ca25 = await fetchLdvSeriesByLocationNames(client, 'emfac2025_county_filtered', californiaVariations);
  }
  if (ca25.length === 0) {
    ca25 = await fetchCaliforniaAggregateFromCounties(client, 'emfac2025_county_filtered');
  }

  return {
    countyData: mergeLdvYearSeries(county25, county21),
    californiaData: mergeLdvYearSeries(ca25, ca21),
  };
}

/** Test / long-running jobs: clear cached EMFAC series. */
export function clearMergedEmfacLdvFactorsCache(): void {
  mergedSeriesCache.clear();
}
